/**
 * BCE Sticky Contact Module
 * 
 * Creates a sticky contact bar that appears when the original contact section
 * scrolls out of view. Shows headline and call-to-action button.
 * 
 * Features:
 * - Intersection Observer for performance
 * - Responsive design
 * - Smooth animations
 * - Auto-cleanup and memory management
 * - Instance management for multiple sections
 * 
 * @class BceStickyContact
 */
class BceStickyContact {
    constructor() {
        this.instances = new WeakMap();
        this.autoInit = false;
    }

    /**
     * Initialize all sticky contact elements found in the DOM
     * Automatically called by BCE system on page load
     */
    init() {
        const stickyContainers = document.querySelectorAll('.bce_sticky-contact');
        stickyContainers.forEach(container => {
            if (!this.instances.has(container)) {
                this.create(container);
            }
        });
    }

    /**
     * Create a sticky contact instance for a container
     * 
     * @param {HTMLElement} container - The container element with bce_sticky-contact class
     * @returns {Object|null} Sticky contact instance or null
     */
    create(container) {
        if (this.instances.has(container)) {
            return this.instances.get(container);
        }

        // Extract content from original container
        const headline = container.querySelector('h2')?.textContent || '';
        const originalButton = container.querySelector('a.btn');
        const button = originalButton?.cloneNode(true);
        
        if (!button || !headline) {
            console.warn('Sticky Contact: Missing headline or button in container', container);
            return null;
        }

        // Create sticky bar structure
        const stickyBar = this.createStickyBar(headline, button);
        document.body.appendChild(stickyBar);

        // Set up visibility management
        const visibilityManager = this.setupVisibilityManager(container, stickyBar);

        // Create instance object
        const instance = {
            container,
            stickyBar,
            headline,
            button: button.cloneNode(true),
            observer: visibilityManager.observer,
            scrollHandler: visibilityManager.scrollHandler,
            destroy: () => {
                this.destroyInstance(container);
            }
        };

        // Store instance
        this.instances.set(container, instance);

        return instance;
    }

    /**
     * Create the sticky bar DOM structure
     * 
     * @param {string} headline - The headline text
     * @param {HTMLElement} button - The cloned button element
     * @returns {HTMLElement} The sticky bar element
     */
    createStickyBar(headline, button) {
        const stickyBar = document.createElement('div');
        stickyBar.className = 'bce_sticky-contact-bar';

        const inner = document.createElement('div');
        inner.className = 'bce_sticky-contact-bar-inner';

        const textDiv = document.createElement('div');
        textDiv.className = 'bce_sticky-contact-bar-text';
        textDiv.textContent = headline;

        // Ensure button retains all original classes and styles

        inner.appendChild(textDiv);
        inner.appendChild(button);
        stickyBar.appendChild(inner);

        return stickyBar;
    }

    /**
     * Set up visibility management with Intersection Observer and scroll fallback
     * 
     * @param {HTMLElement} container - Original container element
     * @param {HTMLElement} stickyBar - Sticky bar element
     * @returns {Object} Observer and scroll handler references
     */
    setupVisibilityManager(container, stickyBar) {
        const updateVisibility = () => {
            const rect = container.getBoundingClientRect();
            const inViewport = rect.top < window.innerHeight && rect.bottom > 0;

            if (!inViewport) {
                stickyBar.classList.add('visible');
            } else {
                stickyBar.classList.remove('visible');
            }
        };

        // Use Intersection Observer for better performance
        const observer = new IntersectionObserver(() => {
            updateVisibility();
        }, {
            root: null,
            threshold: 0,
            rootMargin: '0px'
        });

        observer.observe(container);

        // Scroll handler as fallback for edge cases
        const scrollHandler = () => {
            updateVisibility();
        };

        window.addEventListener('scroll', scrollHandler, { passive: true });

        // Initial check - run immediately and after a short delay
        updateVisibility();
        setTimeout(updateVisibility, 100);

        return { observer, scrollHandler };
    }

    /**
     * Destroy a sticky contact instance
     * 
     * @param {HTMLElement} container - The original container
     */
    destroyInstance(container) {
        const instance = this.instances.get(container);
        if (!instance) return;

        // Clean up observer
        if (instance.observer) {
            instance.observer.disconnect();
        }

        // Clean up scroll handler
        if (instance.scrollHandler) {
            window.removeEventListener('scroll', instance.scrollHandler);
        }

        // Remove sticky bar from DOM
        if (instance.stickyBar && instance.stickyBar.parentNode) {
            instance.stickyBar.parentNode.removeChild(instance.stickyBar);
        }

        // Remove from instances map
        this.instances.delete(container);
    }


    /**
     * Get instance for external access
     * 
     * @param {HTMLElement} container - The original container
     * @returns {Object|null} Sticky contact instance or null
     */
    getInstance(container) {
        return this.instances.get(container);
    }

    /**
     * Update sticky bar content dynamically
     * 
     * @param {HTMLElement} container - The original container
     * @param {string} newHeadline - New headline text
     * @param {HTMLElement} newButton - New button element
     */
    updateContent(container, newHeadline, newButton) {
        const instance = this.instances.get(container);
        if (!instance) return;

        const textElement = instance.stickyBar.querySelector('.bce_sticky-contact-bar-text');
        const buttonElement = instance.stickyBar.querySelector('.btn');

        if (textElement && newHeadline) {
            textElement.textContent = newHeadline;
        }

        if (buttonElement && newButton) {
            const newBtn = newButton.cloneNode(true);
            newBtn.classList.add('sticky-btn');
            buttonElement.parentNode.replaceChild(newBtn, buttonElement);
        }
    }
}

export default new BceStickyContact();